import axios from 'axios';

// Same-origin API — unified Cloud Run deployment
const API_BASE_URL = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const isAuthEndpoint = (url = '') =>
  url.includes('/accounts/login/') ||
  url.includes('/accounts/register/') ||
  url.includes('/accounts/token/refresh/');

const isProtectedAppPath = (pathname = '') =>
  pathname.startsWith('/admin') || pathname.startsWith('/member');

const clearAuthStorage = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isAuthEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/accounts/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        localStorage.setItem('access_token', access);

        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearAuthStorage();
        if (originalRequest.headers) {
          delete originalRequest.headers.Authorization;
        }
        if (isProtectedAppPath(window.location.pathname)) {
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/accounts/login/', credentials),
  register: (userData) => api.post('/accounts/register/', userData),
  logout: (refreshToken) => api.post('/accounts/logout/', { refresh: refreshToken }),
  getProfile: () => api.get('/accounts/profile/'),
  updateProfile: (data) => api.put('/accounts/profile/update/', data),
  changePassword: (passwords) => api.post('/accounts/profile/change-password/', passwords),
  getQRCode: () => api.get('/accounts/profile/qr-code/'),
  totpSetup: () => api.post('/accounts/admin/totp/setup/'),
  totpConfirm: (code) => api.post('/accounts/admin/totp/confirm/', { code }),
  totpReset: (userId) => api.post(`/accounts/admin/staff/${userId}/totp-reset/`),
  getUserByPhone: (phone) => api.get(`/accounts/users/by-phone/${phone}/`),
};

// Blog API
export const blogAPI = {
  getPosts: (params) => api.get('/blog/', { params }),
  getPost: (id) => api.get(`/blog/${id}/`),
  getLatest: () => api.get('/blog/latest/'),
  getFeatured: () => api.get('/blog/featured/'),
  getCategories: () => api.get('/blog/categories/'),
  
  // Admin
  adminGetPosts: () => api.get('/blog/admin/posts/'),
  adminGetPost: (id) => api.get(`/blog/admin/posts/${id}/`),
  adminCreatePost: (data) => api.post('/blog/admin/posts/', data),
  adminUpdatePost: (id, data) => api.put(`/blog/admin/posts/${id}/`, data),
  adminPatchPost: (id, data) => api.patch(`/blog/admin/posts/${id}/`, data),
  adminDeletePost: (id) => api.delete(`/blog/admin/posts/${id}/`),
};

// Core API (Issues, Contact, About)
export const coreAPI = {
  getCouncilors: () => api.get('/core/councilors/'),
  getIssues: () => api.get('/core/issues/'),
  getIssue: (slug) => api.get(`/core/issues/${slug}/`),
  getFeaturedIssues: () => api.get('/core/issues/featured/'),
  getAbout: () => api.get('/core/about/'),
  getOfficeInfo: () => api.get('/core/office-info/'),
  sendMessage: (data) => api.post('/core/contact/', data),
  getPage: (slug) => api.get(`/core/pages/${slug}/`),
  getOrganizationMembers: () => api.get('/core/organization-members/'),
  
  // Admin
  adminGetIssues: () => api.get('/core/admin/issues/'),
  adminGetIssue: (id) => api.get(`/core/admin/issues/${id}/`),
  adminCreateIssue: (data) => api.post('/core/admin/issues/', data),
  adminUpdateIssue: (id, data) => api.put(`/core/admin/issues/${id}/`, data),
  adminDeleteIssue: (id) => api.delete(`/core/admin/issues/${id}/`),
  adminGetMessages: () => api.get('/core/admin/messages/'),
  adminUpdateMessage: (id, data) => api.put(`/core/admin/messages/${id}/`, data),
  adminPatchMessage: (id, data) => api.patch(`/core/admin/messages/${id}/`, data),
  adminGetOrganization: () => api.get('/core/admin/organization/'),
  adminUpdateOrganization: (data) => api.put('/core/admin/organization/', data),
  adminUploadImage: (formData) => api.post('/core/admin/upload-image/', formData),
  adminUploadVideo: (formData, config) => api.post('/core/admin/upload-video/', formData, config),
  adminUploadHeroVideo: (formData, config) => api.post('/core/admin/upload-hero-video/', formData, config),
  createVideoUploadSession: (data) => api.post('/core/admin/video-upload-session/', data),
  completeVideoUpload: (data) => api.post('/core/admin/video-upload-complete/', data),
  adminDeleteHeroVideo: () => api.delete('/core/admin/hero-video/'),
  adminGetOrganizationMembers: () => api.get('/core/admin/organization-members/'),
  adminGetOrganizationMember: (id) => api.get(`/core/admin/organization-members/${id}/`),
  adminCreateOrganizationMember: (data) => api.post('/core/admin/organization-members/', data),
  adminUpdateOrganizationMember: (id, data) => api.put(`/core/admin/organization-members/${id}/`, data),
  adminPatchOrganizationMember: (id, data) => api.patch(`/core/admin/organization-members/${id}/`, data),
  adminDeleteOrganizationMember: (id) => api.delete(`/core/admin/organization-members/${id}/`),
};

export const MAX_VIDEO_SIZE = 300 * 1024 * 1024;

const putFileToGcs = (url, file, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (onProgress) onProgress(event);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`影片上傳失敗（${xhr.status}）`));
    };
    xhr.onerror = () => reject(new Error('影片上傳失敗'));
    xhr.send(file);
  });

const uploadViaCloudRun = async (file, purpose, onProgress) => {
  const formData = new FormData();
  formData.append('video', file);
  const config = { onUploadProgress: onProgress, timeout: 0 };
  const response =
    purpose === 'hero'
      ? await coreAPI.adminUploadHeroVideo(formData, config)
      : await coreAPI.adminUploadVideo(formData, config);
  return {
    url: response.data.home_hero_video || response.data.url,
    home_hero_video: response.data.home_hero_video,
    object_name: response.data.object_name,
  };
};

export async function uploadVideoFile(file, purpose, onProgress) {
  const sessionRes = await coreAPI.createVideoUploadSession({
    filename: file.name,
    content_type: file.type || 'video/mp4',
    size: file.size,
    purpose,
  });
  const session = sessionRes.data;

  if (session.strategy !== 'gcs') {
    return uploadViaCloudRun(file, purpose, onProgress);
  }

  try {
    await putFileToGcs(session.upload_url, file, session.content_type, onProgress);
  } catch (err) {
    return uploadViaCloudRun(file, purpose, onProgress);
  }

  const completeRes = await coreAPI.completeVideoUpload({
    object_name: session.object_name,
    purpose,
  });
  return completeRes.data;
}

// Issues API (wraps coreAPI for convenience)
export const issuesAPI = {
  getAll: () => api.get('/core/issues/'),
  adminGetAll: () => api.get('/core/admin/issues/'),
  getById: (id) => api.get(`/core/admin/issues/${id}/`),
  getBySlug: (slug) => api.get(`/core/issues/${slug}/`),
  getFeatured: () => api.get('/core/issues/featured/'),
  create: (data) => api.post('/core/admin/issues/', data),
  update: (id, data) => api.put(`/core/admin/issues/${id}/`, data),
  patch: (id, data) => api.patch(`/core/admin/issues/${id}/`, data),
  delete: (id) => api.delete(`/core/admin/issues/${id}/`),
};

// Members API (Admin only)
export const membersAPI = {
  getAll: () => api.get('/accounts/members/', { params: { page_size: 500 } }),
  getMember: (id) => api.get(`/accounts/members/${id}/`),
  lookup: (q) => api.get('/accounts/members/lookup/', { params: { q } }),
  getByQR: (qrToken) => api.get(`/accounts/members/by-qr/${encodeURIComponent(qrToken)}/`),
  update: (id, data) => api.put(`/accounts/members/${id}/`, data),
  patch: (id, data) => api.patch(`/accounts/members/${id}/`, data),
  regenerateQr: (id) => api.post(`/accounts/members/${id}/regenerate-qr/`),
  preview: (params) => api.get('/accounts/members/preview/', { params }),
  create: (data) => api.post('/accounts/admin/members/', data),
  exportXlsx: () => api.get('/accounts/admin/members/export/', { responseType: 'blob' }),
};

export const advancedAPI = {
  staff: () => api.get('/accounts/admin/staff/'),
  createStaff: (data) => api.post('/accounts/admin/staff/', data),
  updateStaff: (id, data) => api.patch(`/accounts/admin/staff/${id}/`, data),
  resetPassword: (id, password) => api.post(`/accounts/admin/staff/${id}/password/`, { password }),
  audit: (params) => api.get('/accounts/admin/audit/', { params }),
};

export const mailingAPI = {
  signup: (data) => api.post('/accounts/mailing/', data),
  unsubscribe: (token) => api.post(`/accounts/mailing/unsubscribe/${token}/`),
  lookup: (token) => api.get(`/accounts/mailing/unsubscribe/${token}/`),
  adminList: () => api.get('/accounts/admin/mailing/'),
  adminCreate: (data) => api.post('/accounts/admin/mailing/', data),
  exportCsv: () => api.get('/accounts/admin/mailing/export/', { responseType: 'blob' }),
};

export const eventsAPI = {
  adminList: () => api.get('/events/admin/events/'),
  adminGet: (id) => api.get(`/events/admin/events/${id}/`),
  adminCreate: (data) => api.post('/events/admin/events/', data),
  adminUpdate: (id, data) => api.put(`/events/admin/events/${id}/`, data),
  registerMember: (eventId, memberId) => api.post(`/events/admin/events/${eventId}/register-member/`, { member_id: memberId }),
  registrations: (eventId) => api.get(`/events/admin/events/${eventId}/registrations/`),
  attendances: (eventId) => api.get(`/events/admin/events/${eventId}/attendances/`),
  deleteRegistration: (id) => api.delete(`/events/admin/registrations/${id}/`),
  deleteAttendance: (id) => api.delete(`/events/admin/attendances/${id}/`),
  dashboardStats: () => api.get('/events/admin/dashboard-stats/'),
  giftBatches: () => api.get('/events/admin/gift-batches/'),
  createGiftBatch: (data) => api.post('/events/admin/gift-batches/', data),
  checkIn: (data) => api.post('/events/admin/desk/check-in/', data),
  redeem: (data) => api.post('/events/admin/desk/redeem/', data),
  getUpcoming: () => api.get('/events/upcoming/'),
  myRegistrations: () => api.get('/events/my-registrations/'),
  myWaitlist: () => api.get('/events/my-waitlist/'),
  register: (eventId) => api.post('/events/register/', { event: eventId }),
  cancelRegistration: (id) => api.delete(`/events/registrations/${id}/cancel/`),
  waitlist: (eventId, memberId) => api.post('/events/waitlist/', { event_id: eventId, member_id: memberId }),
  adminWaitlist: (eventId) => api.get(`/events/admin/events/${eventId}/waitlist/`),
  promoteWaitlist: (id) => api.post(`/events/admin/waitlist/${id}/promote/`),
  exportEvent: (eventId) => api.get(`/events/admin/events/${eventId}/export/`, { responseType: 'blob' }),
  queueSms: (data) => api.post('/notifications/sms-blast/', data),
  renewalPreview: () => api.get('/notifications/renewals/'),
};

export function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

export default api;
